<script setup>
import {reactive, ref, computed, watch} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";

const props = defineProps({
    taxProfile: {
        type: Object,
        default: () => null,
    },
    vatStatuses: {
        type: Object,
        default: () => ({}),
    },
    countries: {
        type: Array,
        default: () => [],
    },
    // True when the program issues self-billing documents and therefore cannot
    // pay this partner without a complete profile.
    required: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['tax-profile-updated']);

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const form = reactive({
    legal_name: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    vat_status: '',
    vat_id: '',
});

// The taxProfile prop arrives after the profile page finishes loading (async),
// so populate the form whenever it becomes available instead of only at setup.
watch(() => props.taxProfile, (profile) => {
    if (!profile) {
        return;
    }
    form.legal_name = profile.legal_name || '';
    form.address = profile.address || '';
    form.city = profile.city || '';
    form.postal_code = profile.postal_code || '';
    form.country = profile.country || '';
    form.vat_status = profile.vat_status || '';
    form.vat_id = profile.vat_id || '';
}, {immediate: true});

// When the details are mandatory a VAT status must be picked anyway, so
// pre-select the most common one instead of showing an empty select.
watch(() => props.required, (required) => {
    if (required && !form.vat_status) {
        form.vat_status = 'vat_registered';
    }
}, {immediate: true});

const errors = ref({});

const vatStatusOptions = computed(() =>
    Object.entries(props.vatStatuses).map(([value, label]) => ({value, label}))
);

const vatIdRequired = computed(() => form.vat_status === 'vat_registered');

// Suffix shown next to labels when the details are not mandatory, so partners
// on non-self-billing programs know they can skip the section.
const optionalLabel = computed(() => (props.required ? '' : ' (optional)'));

// The section is only submitted (as part of the page's Update button) when the
// partner has actually filled something in, so a plain profile update isn't
// blocked by the tax fields. Completeness is still enforced at payout time.
const sectionIsEmpty = computed(() => {
    // VAT status counts as input only when optional, since a mandatory section
    // pre-selects one and would otherwise never look empty.
    const vatStatusFilled = !props.required && form.vat_status;

    return !form.legal_name && !form.address && !form.city && !form.postal_code
        && !form.country && !form.vat_id && !vatStatusFilled;
});

const getError = (field) => {
    const error = errors.value[field];
    return Array.isArray(error) ? (error[0] || null) : (error || null);
};

// Saves the tax profile via its endpoint and resolves to a boolean success flag
// so the parent can factor it into the combined "Update" result. Never rejects;
// validation errors are surfaced inline.
const save = () => {
    errors.value = {};

    // Nothing entered yet and no saved profile: skip silently.
    if (sectionIsEmpty.value && !props.taxProfile) {
        return Promise.resolve(true);
    }

    // Small-business / not-registered partners don't submit a VAT ID; the field
    // is hidden for them, so drop anything typed under a previous status.
    if (!vatIdRequired.value) {
        form.vat_id = '';
    }

    return axios.post(route('partners_int.tax-profile.update'), form)
        .then(response => {
            if (parseInt(response.data.status || 0) === 1) {
                emit('tax-profile-updated', response.data.tax_profile);
                return true;
            }
            return false;
        })
        .catch(error => {
            if (error.response?.status === 422) {
                errors.value = error.response.data.errors || {};
            } else {
                errors.value = {general: ['An error occurred. Please try again.']};
                console.error(error);
            }
            return false;
        });
};

defineExpose({save});
</script>

<template>
    <div class="space-y-6">
        <div v-if="getError('general')"
             class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm"
             role="alert"
        >
            {{ getError('general') }}
        </div>

        <div class="grid md:grid-cols-2 gap-6">
            <div class="md:col-span-2">
                <label class="block mb-1.5 text-sm">Legal name{{ optionalLabel }}</label>
                <input type="text" v-model="form.legal_name"
                       class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                       :class="getError('legal_name') ? 'border-red-400' : 'border-gray-300'"/>
                <small v-if="getError('legal_name')" class="text-red-600">{{ getError('legal_name') }}</small>
            </div>

            <div class="md:col-span-2">
                <label class="block mb-1.5 text-sm">Street address{{ optionalLabel }}</label>
                <input type="text" v-model="form.address"
                       class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                       :class="getError('address') ? 'border-red-400' : 'border-gray-300'"/>
                <small v-if="getError('address')" class="text-red-600">{{ getError('address') }}</small>
            </div>

            <div>
                <label class="block mb-1.5 text-sm">City{{ optionalLabel }}</label>
                <input type="text" v-model="form.city"
                       class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                       :class="getError('city') ? 'border-red-400' : 'border-gray-300'"/>
                <small v-if="getError('city')" class="text-red-600">{{ getError('city') }}</small>
            </div>

            <div>
                <label class="block mb-1.5 text-sm">ZIP / Postal code{{ optionalLabel }}</label>
                <input type="text" v-model="form.postal_code"
                       class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                       :class="getError('postal_code') ? 'border-red-400' : 'border-gray-300'"/>
                <small v-if="getError('postal_code')" class="text-red-600">{{ getError('postal_code') }}</small>
            </div>

            <div>
                <label class="block mb-1.5 text-sm">Country{{ optionalLabel }}</label>
                <select v-model="form.country"
                        class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                        :class="getError('country') ? 'border-red-400' : 'border-gray-300'">
                    <option value="" :disabled="required">Select a country</option>
                    <option v-for="country in countries" :key="country.value" :value="country.value">
                        {{ country.name }}
                    </option>
                </select>
                <small v-if="getError('country')" class="text-red-600">{{ getError('country') }}</small>
            </div>

            <div>
                <label class="block mb-1.5 text-sm">VAT status{{ optionalLabel }}</label>
                <select v-model="form.vat_status"
                        class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                        :class="getError('vat_status') ? 'border-red-400' : 'border-gray-300'">
                    <option value="" :disabled="required">Select a VAT status</option>
                    <option v-for="option in vatStatusOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
                <small v-if="getError('vat_status')" class="text-red-600">{{ getError('vat_status') }}</small>
            </div>

            <div v-if="vatIdRequired" class="md:col-span-2">
                <label class="block mb-1.5 text-sm">VAT ID</label>
                <input type="text" v-model="form.vat_id" placeholder="Your VAT / tax ID"
                       class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-400"
                       :class="getError('vat_id') ? 'border-red-400' : 'border-gray-300'"/>
                <small v-if="getError('vat_id')" class="text-red-600">{{ getError('vat_id') }}</small>
            </div>
        </div>
    </div>
</template>
